import { useState } from 'react';
import { Avatar, LogoMark, Screen } from '../components/Chrome.js';
import { Icon } from '../components/Icon.js';
import type { Profile } from '../lib/useGame.js';
import { isMuted, setMuted, play } from '../lib/sound.js';
import { color } from '../theme.js';

/**
 * Lobby. The rank badge ("GOLD III") is a pure function of ELO computed
 * server-side by `rankFromElo` — no schema column.
 *
 * The mockup's "▲ +34 30D" trend is deliberately omitted: it needs a windowed
 * query over elo_history that MVP doesn't have, and showing a fake number is
 * worse than showing none.
 */
export function HomeScreen({
  profile,
  onFindMatch,
  onCreateRoom,
  onJoinRoom,
  onSignOut,
  onHowItWorks,
  onShowStats,
  onOpenLab,
}: {
  profile: Profile | null;
  onFindMatch: () => void;
  onCreateRoom: () => void;
  onJoinRoom: (code: string) => void;
  onSignOut: () => void;
  onShowStats: () => void;
  onOpenLab: () => void;
  onHowItWorks: () => void;
}) {
  const [code, setCode] = useState('');
  const linkBtn: React.CSSProperties = {
    border: 0, background: 'transparent', padding: '2px 6px', cursor: 'pointer',
    fontSize: 12, fontWeight: 500, color: 'rgba(234,255,234,.42)',
    textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: 'rgba(166,255,46,.3)',
  };
  const [muted, setMutedState] = useState(isMuted());
  const toggleMute = (): void => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) play('beep'); // confirm audio is on (and unlock the context)
  };

  const stat = (label: string, value: React.ReactNode, sub: React.ReactNode, subColor = 'rgba(234,255,234,.4)') => (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: '16px 8px',
        border: '1px solid rgba(166,255,46,.14)',
        borderRadius: 12,
        background: 'rgba(166,255,46,.04)',
      }}
    >
      <span style={{ fontSize: 10, letterSpacing: '.18em', color: 'rgba(234,255,234,.45)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 32, lineHeight: 1 }}>{value}</span>
      <span style={{ fontSize: 10, letterSpacing: '.14em', color: subColor, fontWeight: 600 }}>{sub}</span>
    </div>
  );

  return (
    <Screen brackets="diagonal">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          padding: 'clamp(14px,3.6cqmin,40px)',
          gap: 16,
          overflowX: 'hidden',
        }}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minWidth: 0, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <LogoMark size={40} />
            <span style={{ fontWeight: 800, fontSize: 'clamp(15px,4.6cqmin,20px)', letterSpacing: '.02em', whiteSpace: 'nowrap' }}>
              <span style={{ color: color.lime }}>Skill</span>Issue<span style={{ color: 'rgba(234,255,234,.55)' }}>Club</span>
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexShrink: 1 }}>
          <button
            type="button"
            onClick={toggleMute}
            title={muted ? 'Unmute sound' : 'Mute sound'}
            aria-label={muted ? 'Unmute sound' : 'Mute sound'}
            style={{
              width: 38, height: 38, borderRadius: 999,
              border: '1px solid rgba(166,255,46,.2)', background: 'rgba(166,255,46,.05)',
              color: muted ? 'rgba(234,255,234,.4)' : color.lime, cursor: 'pointer',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >
            <Icon name={muted ? 'volume-off' : 'volume'} size={18} />
          </button>
          <button
            onClick={onSignOut}
            title="Sign out"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
              padding: '7px 10px',
              border: '1px solid rgba(166,255,46,.2)',
              borderRadius: 999,
              background: 'rgba(166,255,46,.05)',
            }}
          >
            <Avatar name={profile?.displayName ?? 'You'} side="you" size={30} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, alignItems: 'flex-start', minWidth: 0 }}>
              <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: '.04em', maxWidth: 'clamp(60px,22cqw,160px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.displayName ?? '…'}
              </span>
              {profile?.isAnonymous && (
                <span style={{ fontSize: 10, letterSpacing: '.1em', color: 'rgba(255,176,32,.85)', fontWeight: 600 }}>GUEST</span>
              )}
            </div>
            <Icon name="logout" size={15} style={{ color: 'rgba(234,255,234,.45)', marginLeft: 2, flexShrink: 0 }} />
          </button>
          </div>
        </div>

        {/* main */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ width: 'min(100%,560px)', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 11, letterSpacing: '.34em', fontWeight: 600, color: 'rgba(166,255,46,.6)' }}>READY TO RACE</span>
              <span style={{ fontWeight: 700, fontSize: 'clamp(22px,5cqmin,40px)', letterSpacing: '.02em', lineHeight: 1.08 }}>
                Beat the clock. Beat your rival.
              </span>
            </div>

            <div role="button" tabIndex={0} onClick={onShowStats} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onShowStats(); }} style={{ display: 'flex', gap: 12, cursor: 'pointer' }} title="View your stats">
              {stat('ELO', <span style={{ color: color.lime }}>{profile?.elo ?? '—'}</span>, profile?.rank ?? '—', 'rgba(166,255,46,.75)')}
              {stat(
                'RECORD',
                <>
                  {profile?.wins ?? 0}
                  <span style={{ color: 'rgba(234,255,234,.35)' }}>-</span>
                  {profile?.losses ?? 0}
                </>,
                'W - L',
              )}
              {stat(
                'WIN RATE',
                <>
                  {profile?.winRate ?? 0}
                  <span style={{ fontSize: '.6em', color: 'rgba(234,255,234,.4)' }}>%</span>
                </>,
                `${(profile?.wins ?? 0) + (profile?.losses ?? 0)} PLAYED`,
              )}
            </div>

            <button
              onClick={onFindMatch}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '22px 26px',
                borderRadius: 15,
                background: `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`,
                color: color.limeInk,
                animation: 'ctaGlow 2.2s ease-in-out infinite',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                <span style={{ fontWeight: 700, fontSize: 'clamp(19px,4.2cqmin,34px)', letterSpacing: '.02em', lineHeight: 1 }}>FIND MATCH</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, fontSize: 11, letterSpacing: '.06em', color: 'rgba(7,17,10,.7)' }}>
                  RANKED
                </span>
              </div>
              <span style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>›</span>
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(234,255,234,.12)' }} />
              <span style={{ fontSize: 11, letterSpacing: '.2em', color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>OR PLAY A FRIEND</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(234,255,234,.12)' }} />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, minWidth: 0 }}>
              <button
                onClick={onCreateRoom}
                style={{
                  flex: '1 1 130px',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  border: '1px solid rgba(166,255,46,.3)',
                  borderRadius: 12,
                  background: 'rgba(166,255,46,.06)',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: '.04em', color: color.limeBright }}>CREATE ROOM</span>
              </button>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (code.trim()) onJoinRoom(code.trim().toUpperCase());
                }}
                style={{
                  flex: '1 1 130px',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 8px 8px 16px',
                  border: '1px solid rgba(234,255,234,.14)',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,.03)',
                }}
              >
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                  placeholder="CODE"
                  aria-label="Room code"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    fontFamily: "'JetBrains Mono',monospace",
                    fontWeight: 700,
                    fontSize: 17,
                    letterSpacing: '.2em',
                    color: code ? color.text : 'rgba(234,255,234,.4)',
                  }}
                />
                <button
                  type="submit"
                  disabled={code.length === 0}
                  style={{ padding: '11px 16px', borderRadius: 10, background: 'rgba(166,255,46,.14)' }}
                >
                  <span style={{ fontWeight: 700, fontSize: 14, color: color.limeBright, letterSpacing: '.04em' }}>JOIN</span>
                </button>
              </form>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 18 }}>
          <button type="button" onClick={onHowItWorks} style={linkBtn}>
            How it works
          </button>
          {/* The Game Lab is a dev-only tool (it runs server engines in-browser and
              isn't shipped in the prod client build). Hide its entry point outside dev. */}
          {import.meta.env.DEV && (
            <button type="button" onClick={onOpenLab} style={{ ...linkBtn, textDecorationColor: 'rgba(255,176,32,.4)' }}>
              Game lab
            </button>
          )}
        </div>

        {profile?.isAnonymous && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color.amber, boxShadow: `0 0 8px ${color.amber}` }} />
            <span style={{ fontSize: 12, color: 'rgba(234,255,234,.4)', fontWeight: 500 }}>
              Playing as guest — link an account to keep your rank
            </span>
          </div>
        )}
      </div>
    </Screen>
  );
}
